// visual_doc — les retouches visuelles de l'opérateur, comme document.
//
// ── POURQUOI UN FICHIER DE DONNÉES ET PAS UNE RÉÉCRITURE DU CODE ────────────
//
// L'autre voie était de retrouver la balise dans le TSX et d'y poser un style. Écartée pour
// trois raisons, dans cet ordre d'importance : l'agent REMPLACE ce fichier au run suivant, donc
// la retouche disparaîtrait sans explication ; il faudrait un analyseur syntaxique ; et le
// résultat serait illisible dans le code que l'agent relit.
//
// Ici les retouches vivent dans `frontend/visual.json`, un quatrième fichier racine de l'app,
// exactement au même titre que `theme.json` — la PLATEFORME l'écrit, l'agent n'y touche jamais,
// le deployer l'écrit avant le build, et une VERSION l'enregistre (les deux listes, tenues
// d'accord par app_root_files_parity.test.ts : `theme.json` a manqué aux deux, et trois apps
// ont été publiées avec le préréglage de la plateforme au lieu de leur thème).
//
// ── LES BORNES NE SONT PAS ICI ──────────────────────────────────────────────
//
// Elles sont importées de visual_edit.ts, où vit la géométrie qui les produit. Deux copies
// finiraient par divergence, et le geste bornerait à 100 % là où le fichier accepterait 400 %.
//
// ── RIEN NE LÈVE ────────────────────────────────────────────────────────────
//
// Le fichier arrive du disque : tronqué, édité à la main, ou écrit par une version plus
// ancienne. Chaque cas donne un document vide ou amputé du champ fautif, jamais une erreur —
// une app qui ne démarre pas à cause d'une retouche serait un remède pire que le mal.

import { RATIO_MAX, RATIO_MIN, WIDTH_MIN_PCT } from "./visual_edit";

/** Les retouches d'UNE photo. Chaque champ est OPTIONNEL, et son absence veut dire « ne touche
 *  pas » — pas « remets le défaut ». La différence est tout : `widthPct` absent laisse la
 *  largeur que l'agent a écrite, `widthPct: 100` l'écrase. */
export type PictureOverride = {
  /** `object-position`, en pourcentages. Les DEUX axes ou aucun : la propriété en prend deux. */
  readonly focal?: { readonly x: number; readonly y: number };
  /** `width`, en pourcentage du conteneur. */
  readonly widthPct?: number;
  /** `aspect-ratio`, largeur / hauteur. */
  readonly ratio?: number;
};

export type VisualDoc = { readonly pictures: Readonly<Record<string, PictureOverride>> };

/** Combien de photos retouchées un fichier peut porter. Il part à CHAQUE déploiement et une
 *  version l'enregistre : sans plafond, il grossirait sans fin. */
export const VISUAL_MAX_PICTURES = 200;

/** La longueur maximale d'un nom de photo. */
export const VISUAL_MAX_NAME = 64;

/** Un nom de photo : ce que l'agent écrit dans `<Picture name="…">`. Volontairement étroit —
 *  il sert de clé et il est relu par des humains, donc ni espace, ni barre oblique, ni saut de
 *  ligne. Un nom refusé n'est pas corrigé en silence : l'entrée est écartée. */
const NOM_VALABLE = /^[a-zA-Z0-9][a-zA-Z0-9_-]{0,63}$/;

const borner = (n: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, n));

/** Un nombre fini, ou `undefined` — et non un défaut : un champ illisible doit DISPARAÎTRE,
 *  pas prendre une valeur que personne n'a choisie. */
const nombre = (v: unknown): number | undefined =>
  typeof v === "number" && Number.isFinite(v) ? v : undefined;

/** Les retouches d'une photo, bornées. Fermé CHAMP PAR CHAMP : un `ratio` illisible ne doit
 *  pas faire perdre une position parfaitement valable. */
function override(brut: unknown): PictureOverride {
  if (brut === null || typeof brut !== "object") return {};
  const o = brut as Record<string, unknown>;
  const out: { focal?: { x: number; y: number }; widthPct?: number; ratio?: number } = {};

  const f = o.focal;
  if (f !== null && typeof f === "object") {
    const x = nombre((f as Record<string, unknown>).x);
    const y = nombre((f as Record<string, unknown>).y);
    // LES DEUX AXES OU AUCUN : `object-position` prend deux valeurs, et n'en garder qu'une
    // écrirait un style incomplet.
    if (x !== undefined && y !== undefined) {
      out.focal = { x: Math.round(borner(x, 0, 100)), y: Math.round(borner(y, 0, 100)) };
    }
  }
  const w = nombre(o.widthPct);
  if (w !== undefined) out.widthPct = Math.round(borner(w, WIDTH_MIN_PCT, 100));
  const r = nombre(o.ratio);
  if (r !== undefined) out.ratio = borner(r, RATIO_MIN, RATIO_MAX);
  return out;
}

const aQuelqueChose = (o: PictureOverride): boolean =>
  o.focal !== undefined || o.widthPct !== undefined || o.ratio !== undefined;

/**
 * PURE. Le document, depuis ce que le disque a rendu — du texte JSON ou déjà un objet.
 *
 * NE LÈVE JAMAIS. Tout ce qui n'est pas lisible devient un document vide, et une entrée
 * fautive est écartée sans emporter les autres.
 */
export function parseVisual(raw: unknown): VisualDoc {
  let brut: unknown = raw;
  if (typeof raw === "string") {
    try { brut = JSON.parse(raw); } catch { return { pictures: {} }; }
  }
  if (brut === null || typeof brut !== "object") return { pictures: {} };
  const p = (brut as Record<string, unknown>).pictures;
  // LE TABLEAU EST REFUSÉ ICI, et ici seulement. Une mutation a montré que le même garde sur
  // `brut` était INATTEIGNABLE : un tableau venu de JSON ne porte jamais de propriété nommée,
  // donc `.pictures` y vaut `undefined` et la ligne suivante l'arrête de toute façon. Celui-ci,
  // lui, est porteur — sans lui les INDEX d'un `{"pictures": [...]}` passent le filtre des noms
  // (« 0 » est valable) et le fichier fabriquerait des retouches sur des photos inexistantes.
  if (p === null || typeof p !== "object" || Array.isArray(p)) return { pictures: {} };

  const pictures: Record<string, PictureOverride> = {};
  let compte = 0;
  // L'ORDRE DES CLÉS EST CELUI DU FICHIER, et le plafond coupe à la fin : garder les PREMIÈRES
  // est prévisible, alors qu'un tri changerait lesquelles survivent d'une écriture à l'autre.
  for (const nom of Object.keys(p as Record<string, unknown>)) {
    if (compte >= VISUAL_MAX_PICTURES) break;
    if (nom.length > VISUAL_MAX_NAME || !NOM_VALABLE.test(nom)) continue;
    pictures[nom] = override((p as Record<string, unknown>)[nom]);
    compte += 1;
  }
  return { pictures };
}

/** Une retouche demandée. `null` sur un champ veut dire « retire-le » — c'est le
 *  « remets comme avant », et il doit être dicible autrement que par une valeur par défaut. */
export type OverridePatch = {
  readonly focal?: { readonly x: number; readonly y: number } | null;
  readonly widthPct?: number | null;
  readonly ratio?: number | null;
};

/** Les trois seuls champs d'une retouche. UNE liste, ici, là où le type est défini : le pont
 *  et l'endpoint HTTP la lisaient chacun de leur côté, et deux listes auraient laissé passer
 *  un champ de plus d'un côté que de l'autre. */
const CHAMPS_PATCH = ["focal", "widthPct", "ratio"] as const;

/**
 * PURE. Une retouche réduite aux champs connus, ou `null` si ce n'en est pas une.
 *
 * Elle ne BORNE rien — c'est le travail de `withOverride`, du côté qui écrit. Elle écarte
 * seulement ce qu'on ne connaît pas : un champ inconnu finirait dans le fichier de l'app, et
 * une retouche arrive d'une autre fenêtre ou d'un corps HTTP. `null` traverse : c'est le
 * « remets comme avant ».
 */
export function cleanPatch(raw: unknown): OverridePatch | null {
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const c of CHAMPS_PATCH) if (c in o) out[c] = o[c];
  return out as OverridePatch;
}

/**
 * PURE. Le document après une retouche, bornée à l'écriture et pas seulement à la lecture.
 *
 * FUSION et non remplacement : déplacer une photo puis la redimensionner ne doit pas perdre le
 * déplacement. Une entrée vidée DISPARAÎT, sinon le fichier accumule des coquilles vides qui
 * partent à chaque déploiement.
 *
 * Le document reçu n'est jamais modifié sur place.
 */
export function withOverride(doc: VisualDoc, name: string, patch: OverridePatch): VisualDoc {
  if (name.length > VISUAL_MAX_NAME || !NOM_VALABLE.test(name)) return doc;
  const avant = doc.pictures[name] ?? {};
  const fusion: Record<string, unknown> = { ...avant };
  for (const champ of CHAMPS_PATCH) {
    if (!(champ in patch)) continue;
    const v = patch[champ];
    if (v === null) delete fusion[champ];
    else fusion[champ] = v;
  }
  const propre = override(fusion);
  const pictures = { ...doc.pictures };
  if (aQuelqueChose(propre)) pictures[name] = propre;
  else delete pictures[name];
  return { pictures };
}

/**
 * PURE. Le fichier à écrire.
 *
 * CLÉS TRIÉES : deux fois le même état doit donner les MÊMES octets, sinon un déploiement
 * diffère pour un état identique et une version enregistre du bruit.
 */
export function serializeVisual(doc: VisualDoc): string {
  const trie: Record<string, PictureOverride> = {};
  for (const nom of Object.keys(doc.pictures).sort()) trie[nom] = doc.pictures[nom]!;
  return `${JSON.stringify({ pictures: trie }, null, 2)}\n`;
}

/**
 * PURE. Le style que l'app applique à une photo.
 *
 * AUCUNE UNITÉ D'ÉCRAN NE SORT D'ICI, et c'est toute la garantie de responsivité : des
 * pourcentages et un ratio suivent la largeur de la page tout seuls. Un `px` à cette place
 * serait exactement le défaut que la fonction existe pour éviter.
 *
 * Un champ absent ne produit RIEN : le CSS que l'agent a écrit reste en place.
 */
export function pictureStyle(o: PictureOverride | undefined): Record<string, string> {
  const s: Record<string, string> = {};
  if (o === undefined) return s;
  if (o.focal !== undefined) {
    s.objectPosition = `${o.focal.x}% ${o.focal.y}%`;
    // `cover` AVEC le point visible, et seulement avec lui. La géométrie de visual_edit
    // suppose que l'image DÉBORDE de son cadre — c'est ce débordement que le pourcentage
    // parcourt. Sous `contain` l'image tient dedans, il n'y a rien à faire défiler, et la
    // poignée annoncerait un geste sans effet. Posé ici, l'hypothèse devient vraie là où elle
    // compte ; posé partout, il recadrerait une image que l'agent voulait entière.
    s.objectFit = "cover";
  }
  if (o.widthPct !== undefined) s.width = `${o.widthPct}%`;
  if (o.ratio !== undefined) s.aspectRatio = String(o.ratio);
  return s;
}
